import * as React from 'react';
import { Box, useTheme } from '@rabex-kit/rabex-ui';
import { keyframes } from '@mui/material/styles';
import { getSplashColors } from './splashColors';

const midWave = keyframes`
    0% {
        transform: translateX(-140%) skewX(-18deg);
    }
    100% {
        transform: translateX(140%) skewX(-18deg);
    }
`;

/**
 * Splash backdrop — must render byte-identical to the index.html preboot.
 *
 * Gradient angles use numeric degrees (90 / 270) instead of keyword
 * directions (to right / to left). Numeric angles do not get touched
 * by stylis-plugin-rtl when the sx prop is processed under dir="rtl",
 * so what we write is what the browser paints. 90deg = the same visual
 * direction as `to right` in index.html.
 *
 * The middle-band shimmer is a real <Box> child rather than a CSS
 * ::after pseudo-element. Pseudo-elements painted inside a parent
 * with clip-path occasionally fail to animate their transform on
 * Chromium when the parent's stacking context is fresh — making the
 * shimmer just sit still. A real element renders reliably and is
 * inspectable in DevTools when something does go wrong.
 */
export default function SplashBackdrop() {
  const c = getSplashColors(useTheme().palette.mode);
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden'
      }}>
      {/* Top trapezoid */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(90deg, ${c.bg} 71.98%, ${c.topEnd} 98.71%)`,
          clipPath: 'polygon(0 0, 100% 0, 100% 10.01vh, 0 36.37vh)',
          boxShadow: `inset 0 -1px 0 ${c.edgeShadow}`
        }}
      />

      {/* Bottom trapezoid */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(270deg, ${c.bg} 54.35%, ${c.bottomEnd} 100%)`,
          clipPath:
            'polygon(0 calc(100% - 22.93vh), 100% calc(100% - 49.29vh), 100% 100%, 0 100%)',
          boxShadow: `inset 0 1px 0 ${c.edgeShadow}`
        }}
      />

      {/* Middle parallelogram band */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          clipPath:
            'polygon(0 36.37vh, 100% 10.01vh, 100% calc(100% - 49.29vh), 0 calc(100% - 22.93vh))',
          backgroundColor: c.mid,
          overflow: 'hidden'
        }}>
        {/* Shimmer — real child element, not ::after */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.55,
            background: `linear-gradient(90deg, ${c.shimmerEdge} 0%, ${c.shimmerPeak} 45%, ${c.shimmerEdge} 100%)`,
            animation: `${midWave} 1.6s ease-in-out infinite`,
            willChange: 'transform',
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none'
            }
          }}
        />
      </Box>
    </Box>
  );
}
